import { FormFieldLine } from "../../../../../../components/form-field-line/form-field-line";
import { IOption, SelectDropdown } from "../../../../../../components/inputs/select-dropdown/select-dropdown";
import TaxCalculator from "../tax-calculator/tax-calculator";
import FontBody2 from "../../../../../../components/fonts/font-body2/font-body2";
import { FONT_COLOR } from "../../../../../../components/fonts/font-props";
import currentPortalStore from "../../../../../../stores/current-portal-store";
import { RETURN_LABEL_FEE_PAYMENT } from "../../../../../../models/User";
import FeePaymentSelector from "./fee-payment-selector/fee-payment-selector";
import { Note, NOTE_TYPE } from "../../../../../../components/note/note";
import { FEE_TYPE } from "../../../../policy-types";
import { observer } from "mobx-react";
import PolicyFeeStore from "../../../../stores/policy-fee-store";
import { IValidationGroup } from "../../../../../../hooks/validation-group/validation-group-hook";
import Hr from "../../../../../../components/hr/hr";
import AmountSettings from "../amount-settings/amount-settings";
import { PROFILE_CAPABILITIES } from "../../../../../../models/ProfileCapabilities";
import dashboardSettingsStore from "../../../../../../stores/dashboard-settings-store";
import ResolveCapability from "../../../../../../components/resolve-capability/resolve-capability";
import { FUNC } from "../../../../../../stores/platform-service-store";
import { useEffect, useMemo } from "react";

interface IProps {
    fee: PolicyFeeStore | undefined;
    validationGroup: IValidationGroup;
    hasRCRM: boolean;
    supportedShippingLabelFeeTypes?: FEE_TYPE[];
}

const ShippingLabelFee: React.FC<IProps> = observer(({ fee, validationGroup, hasRCRM, supportedShippingLabelFeeTypes }) => {
	const supportedTypesKey = useMemo(
		() => JSON.stringify(supportedShippingLabelFeeTypes ?? []),
		[supportedShippingLabelFeeTypes]
	);

	const feeTypeOptions = useMemo(() => {
		if (!fee) {
			return [];
		}
		return fee.getShippingLabelOptions(supportedShippingLabelFeeTypes);
	}, [
		fee,
		supportedTypesKey,
		fee?.policy?.isShipByAnyCarrier,
		fee?.policy?.isLabelCostSupported,
	]);

	const isValueEnforced = useMemo(
		() => feeTypeOptions.length === 1 && feeTypeOptions[0].disabled,
		[feeTypeOptions]
	);

	useEffect(() => {
		if (!fee || !isValueEnforced) {
			return;
		}
		const matches =
			feeTypeOptions[0].value === FEE_TYPE.FREE
				? fee.feeType === null
				: fee.feeType === feeTypeOptions[0].value;

		if (!matches) {
			fee.setFeeType(
				feeTypeOptions[0].value === FEE_TYPE.FREE
					? null
					: feeTypeOptions[0].value
			);
		}
	}, [fee, fee?.feeType, isValueEnforced, feeTypeOptions, supportedTypesKey]);

	if (!fee) {
		return null;
	}

	const canChargeTaxForFee = currentPortalStore.platform?.isFunctionalitySupported(FUNC.FUNC_CHARGE_TAXES_FOR_FEES);

	const selectedFeeTypeOption = feeTypeOptions.find(o => {
        /** custom case for FREE shipping label, which in database is marked as null, but for form validation we need value */
        if (o.value === FEE_TYPE.FREE) {
            return fee.feeType === null;
        }

        return o.value === fee.feeType;
    }) as IOption;

    const showReturnLabelFeePaymentSetting = () => {
        if (!fee.isCharged) {
            return false;
        }

        const capability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.RETURN_LABEL_FEE_PAYMENT);
        
        if (!capability.isSupported) {
            return false;
        }

        /** when fee type is one of return shipping label fee types and is free, in other words one of [Free, Not Provided] */
        if (!fee.feeType ||
            ![FEE_TYPE.SHIPMENT_FEE, FEE_TYPE.COST_PLUS_SHIPMENT_FEE, FEE_TYPE.COST_PLUS_ITEM_FEE, FEE_TYPE.COST].includes(fee.feeType)
        ) {
            return false;
        }

        return true;
    }

    return (
        <>
            <div className="policy-fee">
                <FormFieldLine
                    label='Return Shipping Label Fee'
                    note='Set a return shipping label fee to charge and determine how it will be calculated.'
                    tooltip={fee.policy.isShipByAnyCarrier && <div style={{ maxWidth: '300px' }}>
                        <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                            Charge for a return label only if you provide one manually to your customers when approving the return.
                        </FontBody2>
                    </div>}
                >
                    <SelectDropdown
                        value={selectedFeeTypeOption}
                        options={feeTypeOptions}
                        onChange={(v) => {
                            const value = (v?.value && v.value !== FEE_TYPE.FREE) ? v.value as FEE_TYPE : null;
                            fee.setFeeType(value);
                        }}
                        validationGroup={validationGroup}
                        required
                        isSearchable={false}
                        disabled={isValueEnforced}
                    />

                </FormFieldLine>
                { hasRCRM &&
                    <Note
                        type={NOTE_TYPE.INFORMATION}
                        body={<FontBody2>
                            When using a Return Guard shipping method, it's recommended to set the fee to "Cost", since that is what will be charged.
                        </FontBody2>}
                    />
                }
                {fee.isCharged && (
                    <>
                        <FormFieldLine label=''>
                            <AmountSettings fee={fee} validationGroup={validationGroup} />
                        </FormFieldLine>
	                    <ResolveCapability capability={PROFILE_CAPABILITIES.FEE_TAXES_CALCULATION}>
		                    {({ isSupported}) => (isSupported && canChargeTaxForFee) && (
			                    <FormFieldLine
				                    label='Tax Calculation'
				                    labelIndent={1}
			                    >
				                    <TaxCalculator fee={fee} validationGroup={validationGroup} disabled={fee.isTaxForcedNoCharge} />
			                    </FormFieldLine>
		                    )}
	                    </ResolveCapability>
                    </>
                )}
                {fee.showReturnMethodNotSupportCostFee && (
                    <Note
                        type={NOTE_TYPE.INFORMATION}
                        body={<>
                            The return method doesn't support cost-based fees.
                        </>}
                    />
                )}
                <Hr />
            </div>
            {
                showReturnLabelFeePaymentSetting() && (
                    <>
                        {currentPortalStore.setting?.returnLabelFeePayment === RETURN_LABEL_FEE_PAYMENT.SET_PER_POLICY_RULE ? (
                            <FormFieldLine
                                label='Return Label Fee Payment'
                                note='Define how to charge customers for return label fees when relevant.'
                            >
                                <FeePaymentSelector fee={fee} validationGroup={validationGroup} />
                            </FormFieldLine>
                        ) : (
                            currentPortalStore.setting?.returnLabelFeePayment === RETURN_LABEL_FEE_PAYMENT.DEDUCT_FROM_REFUNDS ? (
                                <Note
                                    type={NOTE_TYPE.INFORMATION}
                                    body={<>
                                        The return label fee will be deducted from available refunds.
                                        Customers will be charged for the return label only when the refunds don't cover the fee or there are no available refunds.
                                    </>}
                                />
                            ) : (
                                <Note
                                    type={NOTE_TYPE.INFORMATION}
                                    body={<>Customers will be charged for the full return label fee.</>}
                                />
                            )
                        )}
                    </>
                )
            }
        </>
    );
});

export default ShippingLabelFee;